import {
  addHours,
  addMinutes,
  addSeconds,
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  endOfDay,
  format,
  isSameDay,
  startOfDay,
} from 'date-fns';
import type { User } from '../models';
import type { Activity } from '../models/activity';
import {
  EventResult,
  EventType,
  type DistanceType,
  type Event,
  type RaceEvent,
  type SaveEventPayload,
  type TrainingEvent,
} from '../models/event';
import { dateFromTimestamp } from './data';
import { events } from './static-data';
import { getZonedStartOfDay } from './time';

export function calculateEventEndAt(event: Event | SaveEventPayload): Date | null {
  const startAt = dateFromTimestamp(event.startAt) as Date;
  if (!startAt) {
    throw new Error('Invalid event start time');
  }

  if (event.type === EventType.RACE && (event as RaceEvent).target) {
    const target = (event as RaceEvent).target!;
    let endAt = addHours(startAt, target.hour || 0);
    endAt = addMinutes(endAt, target.minute || 0);
    endAt = addSeconds(endAt, target.second || 0);
    return endAt;
  } else if (event.type === EventType.TRAINING && (event as TrainingEvent).plannedSections) {
    const totalSeconds = (event as TrainingEvent).plannedSections!.reduce((acc, ps) => {
      const groupDuration = ps.sections.reduce((gAcc, s) => gAcc + s.duration, 0);
      return acc + groupDuration * ps.reps;
    }, 0);
    if (totalSeconds > 0) {
      return addSeconds(startAt, totalSeconds);
    }
  }

  return null;
}

export function isEventMatchedToActivity({
  event,
  activity,
  user,
}: {
  event?: Event;
  activity: Activity;
  user: User;
}): boolean {
  if (!event) {
    return false;
  }
  if (
    event.type === EventType.RACE &&
    event.result &&
    [EventResult.CANCELLED, EventResult.CANCELLED_PERSONALLY].includes(event.result)
  ) {
    return false;
  }

  if (activity.type !== 'Run') {
    console.log('[event-matching] Activity type is not Run', activity.type);
    return false;
  }

  const timezone = user.settings?.tz || 'Asia/Tokyo';
  const eventDate = getZonedStartOfDay(dateFromTimestamp(event.startAt) as Date, timezone);
  const activityDate = getZonedStartOfDay(dateFromTimestamp(activity.startDate) as Date, timezone);

  if (isSameDay(eventDate, activityDate)) {
    console.log('[event-matching] Event date is same as activity date', eventDate, activityDate);
    const eventDistance = getDistanceFromEvent(event);
    const activityDistance = activity.distance;

    if (activityDistance >= eventDistance * 0.75) {
      console.log('[event-matching] Event distance is same as activity distance', eventDistance, activityDistance);
      return true;
    } else {
      console.log('[event-matching] Event distance is not same as activity distance', eventDistance, activityDistance);
    }
  } else {
    console.log('[event-matching] Event date is not same as activity date', eventDate, activityDate);
  }

  return false;
}

export function getDistanceFromEvent(event: Event): number {
  if (event.type === EventType.RACE && event.distance) {
    if (event.distance !== 'OTHER') {
      return getDistanceByType(event.distance);
    }
    return event.distanceInMeters || 0;
  }
  return 0;
}

export function getDistanceByType(distance: DistanceType): number {
  switch (distance) {
    case '100M':
      return 100;
    case '200M':
      return 200;
    case '400M':
      return 400;
    case '800M':
      return 800;
    case '1000M':
      return 1000;
    case '1500M':
      return 1500;
    case '3000M':
      return 3000;
    case '5000M':
      return 5000;
    case '10000M':
      return 10000;
    case 'HALF_MARATHON':
      return 21097;
    case 'FULL_MARATHON':
      return 42195;
    case '50KM':
      return 50000;
    case '100KM':
      return 100000;
    case '165KM':
      return 165000;
    case 'TRAIL_RUNNING_S':
      return 10000;
    case 'TRAIL_RUNNING_M':
      return 30000;
    case 'TRAIL_RUNNING_L':
      return 80000;
    case 'TRAIL_RUNNING_XL':
      return 120000;
    case 'OTHER':
    default:
      return 0;
  }
}

export function getDistanceNameByType(distance: DistanceType): string {
  return events.find((d) => d.value === distance)?.title || 'Unknown Distance';
}

export function getColorForDistanceType(distance: DistanceType): string {
  switch (distance) {
    case '100M':
      return '#F2B7A4'; // Coral pink
    case '200M':
      return '#8FC8B4'; // Mint green
    case '400M':
      return '#80B3C7'; // Sky blue
    case '800M':
      return '#F9E572'; // Light yellow
    case '1000M':
      return '#DDA0DD'; // Plum
    case '1500M':
      return '#F4A460'; // Sandy brown
    case '3000M':
      return '#B19CD9'; // Light purple
    case '5000M':
      return '#FFB6C1'; // Light pink
    case '10000M':
      return '#87D3A4'; // Sage green
    case 'HALF_MARATHON':
      return '#F0D785'; // Pale gold
    case 'FULL_MARATHON':
      return '#C8A8E9'; // Lavender
    case '50KM':
      return '#9FD3E7'; // Powder blue
    case '100KM':
      return '#FFD1A9'; // Peach
    case '165KM':
      return '#A8E6CF'; // Mint cream
    case 'TRAIL_RUNNING_S':
      return '#FFC0CB'; // Pink
    case 'TRAIL_RUNNING_M':
      return '#E6E6FA'; // Lavender mist
    case 'TRAIL_RUNNING_L':
      return '#5A8FA3'; // Darker sky blue
    case 'TRAIL_RUNNING_XL':
      return '#B875B8'; // Darker plum
    case 'OTHER':
    default:
      return '#424242';
  }
}

export function getColorForEvent(event: Event): string {
  if (event.type === EventType.RACE && event.distance) {
    return getColorForDistanceType(event.distance);
  }
  return '#757575';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getEventRelativeDateText(event: Event, t: (key: string, params?: any) => string): string {
  const eventDate = endOfDay(dateFromTimestamp(event.startAt) as Date);
  const now = startOfDay(new Date());
  const diffInDays = differenceInDays(eventDate, now);

  if (diffInDays < 0) {
    return t('events.diff.passed');
  } else if (diffInDays === 0) {
    return t('events.diff.today');
  } else if (diffInDays === 1) {
    return t('events.diff.tomorrow');
  } else if (diffInDays <= 7) {
    return t('events.diff.days', { count: diffInDays });
  } else if (diffInDays <= 31) {
    return t('events.diff.weeks', { count: differenceInWeeks(eventDate, now) });
  } else if (diffInDays <= 365) {
    return t('events.diff.months', { count: differenceInMonths(eventDate, now) });
  } else {
    return format(eventDate, t('formats.date.short'));
  }
}
